import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { setupAuthGuards } from '@shared/utils/route-guards'
import { setupChunkErrorHandler } from '@shared/utils/chunk-error-handler'

const Layout = () => import('../layouts/MainLayout.vue')
const LoginView = () => import('../views/LoginView.vue')
const ForbiddenView = () => import('../views/error/ForbiddenView.vue')
const NotFoundView = () => import('../views/error/NotFoundView.vue')
const AccessGuidanceView = () => import('../views/error/AccessGuidanceView.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'EnterpriseLogin',
    component: LoginView,
    meta: { title: '登录', requiresAuth: false }
  },
  {
    path: '/',
    component: Layout,
    redirect: { name: 'EnterpriseDashboard' },
    meta: { requiresAuth: true, roles: ['enterprise'] },
    children: [
      {
        path: 'dashboard',
        name: 'EnterpriseDashboard',
        component: () => import('../views/dashboard/DashboardView.vue'),
        meta: {
          title: '仪表盘',
          requiresAuth: true,
          roles: ['enterprise'],
          permissions: ['enterprise_dashboard:view'],  // 恢复权限检查
          menuId: '/dashboard',
          breadcrumbs: [{ title: '首页' }, { title: '仪表盘' }]
        }
      },
      {
        path: 'supply/menus',
        name: 'EnterpriseSupplyMenu',
        component: () => import('../views/supply/MenuListView.vue'),
        meta: {
          title: '供餐菜单',
          requiresAuth: true,
          roles: ['enterprise'],
          permissions: ['enterprise_supply_menu:view'],
          menuId: '/supply/menus',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '供餐菜单' }]
        }
      },
      // 我的商户模块
      {
        path: 'merchants/restaurants',
        name: 'EnterpriseMyRestaurants',
        component: () => import('../views/merchants/MyRestaurantsView.vue'),
        meta: {
          title: '供餐餐厅',
          requiresAuth: true,
          roles: ['enterprise'],
          // ✅ 移除 permissions 检查：企业管理员访问本企业商户是基本功能，后端 API 已有权限检查
          menuId: '/merchants/restaurants',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '我的商户' }, { title: '供餐餐厅' }]
        }
      },
      {
        path: 'merchants/trial-requests',
        name: 'EnterpriseTrialMealRequests',
        component: () => import('../views/merchants/TrialMealRequestsView.vue'),
        meta: {
          title: '试餐申请',
          requiresAuth: true,
          roles: ['enterprise'],
          // ✅ 移除 permissions 检查：企业管理员访问本企业试餐申请是基本功能，后端 API 已有权限检查
          menuId: '/merchants/trial-requests',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '我的商户' }, { title: '试餐申请' }]
        }
      },
      {
        path: 'merchants/restaurant-info',
        name: 'EnterpriseRestaurantInfo',
        component: () => import('../views/merchants/RestaurantInfoView.vue'),
        meta: {
          title: '餐厅信息',
          requiresAuth: true,
          roles: ['enterprise'],
          menuId: '/merchants/restaurant-info',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '我的商户' }, { title: '餐厅信息' }]
        }
      },
      {
        path: 'merchants/blacklist',
        name: 'EnterpriseBlacklist',
        component: () => import('../views/merchants/BlacklistView.vue'),
        meta: {
          title: '黑名单',
          requiresAuth: true,
          roles: ['enterprise'],
          // ✅ 移除 permissions 检查：企业管理员管理本企业黑名单是基本功能，后端 API 已有权限检查
          menuId: '/merchants/blacklist',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '我的商户' }, { title: '黑名单' }]
        }
      },
      // 订单管理（合并页：预定订单 + 配送信息 + 订单查询 + 订单售后）
      {
        path: 'orders/center',
        name: 'EnterpriseOrderCenter',
        component: () => import('../views/orders/center/EnterpriseOrderCenterView.vue'),
        meta: {
          title: '订单管理',
          requiresAuth: true,
          roles: ['enterprise'],
          menuId: '/orders/center',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '订单管理' }]
        }
      },
      {
        path: 'employees',
        name: 'EnterpriseEmployees',
        component: () => import('../views/employees/EmployeeListView.vue'),
        meta: {
          title: '员工管理',
          requiresAuth: true,
          roles: ['enterprise'],
          permissions: ['enterprise_employees:list'],
          menuId: '/employees',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '员工管理' }]
        }
      },
      {
        path: 'employees/:id/wallet',
        name: 'EmployeeWallet',
        component: () => import('../views/employees/EmployeeWalletView.vue'),
        meta: {
          title: '员工钱包',
          requiresAuth: true,
          roles: ['enterprise'],
          // ✅ 移除 permissions 检查：企业管理员查看本企业员工钱包是基本功能，后端 API 已有权限检查
          menuId: '/employees',  // ⭐ 关联到父级菜单（员工管理）
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '员工管理', path: '/employees' }, { title: '员工钱包' }]
        }
      },
      {
        path: 'employees/nfc-cards',
        name: 'EnterpriseNfcEnrollment',
        component: () => import('../views/employees/NfcEnrollmentView.vue'),
        meta: {
          title: '员工 NFC 卡绑定', requiresAuth: true, roles: ['enterprise'],
          menuId: '/employees',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '员工管理', path: '/employees' }, { title: 'NFC卡绑定' }]
        }
      },
      {
        path: 'finance/recharge-management',
        name: 'EnterpriseRechargeManagement',
        component: () => import('../views/finance/RechargeManagementView.vue'),
        meta: {
          title: '充值管理',
          requiresAuth: true,
          roles: ['enterprise'],
          // 任一权限即可进入（route-guards 使用 some）
          permissions: ['enterprise_wallet:view', 'enterprise_finance:subsidies', 'enterprise_finance:wallet'],
          menuId: '/finance/recharge-management',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '补贴与钱包' }, { title: '充值管理' }]
        }
      },
      {
        path: 'finance/subsidies',
        name: 'EnterpriseSubsidies',
        component: () => import('../views/finance/SubsidiesView.vue'),
        meta: {
          title: '补贴管理',
          requiresAuth: true,
          roles: ['enterprise'],
          permissions: ['enterprise_finance:subsidies'],
          menuId: '/finance/subsidies',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '补贴与钱包' }, { title: '补贴管理' }]
        }
      },
      {
        path: 'finance/subsidy-batches',
        name: 'EnterpriseSubsidyBatches',
        component: () => import('../views/finance/subsidy/SubsidyBatchListView.vue'),
        meta: {
          title: '补贴批次列表',
          requiresAuth: true,
          roles: ['enterprise'],
          permissions: ['enterprise_finance:subsidies'],
          menuId: '/finance/subsidy-batches',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '补贴与钱包' }, { title: '补贴批次列表' }]
        }
      },
      {
        path: 'finance/wallet-config',
        name: 'EnterpriseWalletConfig',
        component: () => import('../views/finance/wallet/WalletListView.vue'),
        meta: {
          title: '钱包配置',
          requiresAuth: true,
          roles: ['enterprise'],
          permissions: ['enterprise_wallet:view'],
          menuId: '/finance/wallet-config',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '补贴与钱包' }, { title: '钱包配置' }]
        }
      },
      {
        path: 'finance/wallet',
        name: 'EnterpriseWallet',
        component: () => import('../views/finance/wallet/TransactionRecords.vue'),
        meta: {
          title: '钱包流水',
          requiresAuth: true,
          roles: ['enterprise'],
          permissions: ['enterprise_finance:wallet'],
          menuId: '/finance/wallet',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '补贴与钱包' }, { title: '钱包流水' }]
        }
      },
      {
        path: 'finance/subsidy-transfer',
        name: 'EnterpriseSubsidyTransfer',
        component: () => import('../views/finance/SubsidyTransferView.vue'),
        meta: {
          title: '补贴转赠',
          requiresAuth: true,
          roles: ['enterprise'],
          // ✅ 移除 permissions 检查：企业管理员/员工访问补贴转赠是基本功能，后端 API 已有权限检查
          menuId: '/finance/subsidy-transfer',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '补贴与钱包' }, { title: '补贴转赠' }]
        }
      },
      // 财务对账模块（原 对账与统计）
      {
        path: 'finance/reconciliation',
        name: 'EnterpriseReconciliation',
        component: () => import('../views/finance/ReconciliationView.vue'),
        meta: {
          title: '财务对账',
          requiresAuth: true,
          roles: ['enterprise'],
          // ✅ 移除 permissions 检查：企业管理员访问财务对账是基本功能
          menuId: '/finance/reconciliation',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '财务对账' }]
        }
      },
      // 统计分析模块（新增）
      {
        path: 'analytics',
        name: 'EnterpriseAnalytics',
        component: () => import('../views/analytics/AnalyticsView.vue'),
        meta: {
          title: '统计分析',
          requiresAuth: true,
          roles: ['enterprise'],
          // ✅ 移除 permissions 检查：企业管理员访问统计分析是基本功能
          menuId: '/analytics',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '统计分析' }]
        }
      },
      {
        path: 'calendar',
        name: 'EnterpriseCalendar',
        component: () => import('../views/calendar/EnterpriseCalendarView.vue'),
        meta: {
          title: '企业日历',
          requiresAuth: true,
          roles: ['enterprise'],
          permissions: ['enterprise_calendar:view'],
          menuId: '/calendar',
          breadcrumbs: [{ title: '首页', path: '/dashboard' }, { title: '企业日历' }]
        }
      },
      {
        path: 'profile',
        name: 'EnterpriseProfile',
        component: () => import('../views/common/PlaceholderView.vue'),
        meta: { title: '个人资料', requiresAuth: true }
      },
      {
        path: 'settings',
        name: 'EnterpriseSettings',
        component: () => import('../views/common/PlaceholderView.vue'),
        meta: { title: '系统设置', requiresAuth: true }
      }
    ]
  },
  {
    path: '/access-guidance',
    name: 'EnterpriseAccessGuidance',
    component: AccessGuidanceView,
    meta: { title: '访问指引', requiresAuth: false }
  },
  {
    path: '/403',
    name: 'EnterpriseForbidden',
    component: ForbiddenView,
    meta: { title: '无权访问', requiresAuth: false }
  },
  {
    path: '/404',
    name: 'EnterpriseNotFound',
    component: NotFoundView,
    meta: { title: '页面不存在', requiresAuth: false }
  },
  { path: '/:pathMatch(.*)*', redirect: '/404' }
]

const router = createRouter({
  history: createWebHistory('/enterprise-admin/'),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    return { top: 0 }
  }
})

setupAuthGuards(router, { loginPath: '/login', homePath: '/', requireRoles: ['enterprise'], guidancePath: '/access-guidance' })

// ⭐ 设置 chunk 加载失败自动恢复（部署新版本后，旧 chunk 404 时自动刷新）
setupChunkErrorHandler(router)

export default router
